import { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { createApiUrl } from '../../../utils/apiConfig';
import { getAuthHeaders } from '../../../utils/authUtils';

export default function useAISummary({ patientId, examId, initialPromptName, initialCustomPrompt, enabled = true }) {
  const { i18n } = useTranslation();
  const currentLang = (i18n.language || 'de').substring(0, 2);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [error, setError] = useState(null);
  const [activePromptName, setActivePromptName] = useState(initialPromptName || null);
  const [activeCustomPrompt, setActiveCustomPrompt] = useState(initialCustomPrompt || null);
  const [activeExamId, setActiveExamId] = useState(examId || null);
  const [userPrompts, setUserPrompts] = useState([]);
  const pollingRef = useRef(null);

  const effectivePromptName = activePromptName || '__system__';

  useEffect(() => {
    if (!enabled) return undefined;
    const loadPrompts = async () => {
      try {
        const response = await fetch(createApiUrl('profile/ai-settings'), {
          headers: getAuthHeaders(),
        });
        if (response.ok) {
          const data = await response.json();
          setUserPrompts(data.ai_settings?.prompts || []);
        }
      } catch (e) {
        console.error('[useAISummary] Error loading prompts:', e);
      }
    };
    loadPrompts();
    return undefined;
  }, [enabled]);

  const fetchSummary = useCallback(async () => {
    if (!patientId || !enabled) return null;
    try {
      let url = `ai-summary/patient/${patientId}?prompt_name=${encodeURIComponent(effectivePromptName)}`;
      if (activeExamId) url += `&exam_id=${activeExamId}`;
      const response = await fetch(createApiUrl(url), { headers: getAuthHeaders() });
      if (response.ok) {
        const data = await response.json();
        setSummary(data);
        setError(null);
        return data;
      }
      if (response.status === 404) return null;
      throw new Error(`HTTP ${response.status}`);
    } catch (err) {
      console.error('[useAISummary] Fetch error:', err);
      return null;
    }
  }, [patientId, effectivePromptName, activeExamId, enabled]);

  const pollStatus = useCallback(async () => {
    if (!patientId || !enabled) return;
    try {
      let statusUrl = `ai-summary/patient/${patientId}/status?prompt_name=${encodeURIComponent(effectivePromptName)}`;
      if (activeExamId) statusUrl += `&exam_id=${activeExamId}`;
      const response = await fetch(createApiUrl(statusUrl), { headers: getAuthHeaders() });
      if (response.ok) {
        const data = await response.json();
        if (data.status === 'COMPLETED' || data.status === 'ERROR') {
          clearInterval(pollingRef.current);
          pollingRef.current = null;
          await fetchSummary();
          setRegenerating(false);
        }
      }
    } catch (err) {
      console.error('[useAISummary] Poll error:', err);
    }
  }, [patientId, fetchSummary, effectivePromptName, activeExamId, enabled]);

  const startGeneration = useCallback(async (language = currentLang) => {
    if (!patientId || !enabled) return;
    setRegenerating(true);
    setError(null);
    try {
      const body = { prompt_name: effectivePromptName };
      if (activeCustomPrompt) body.custom_prompt = activeCustomPrompt;
      if (activeExamId) body.exam_id = parseInt(activeExamId, 10);

      const response = await fetch(createApiUrl(`ai-summary/patient/${patientId}/generate?language=${language}`), {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.detail || `HTTP ${response.status}`);
      }
      if (pollingRef.current) clearInterval(pollingRef.current);
      pollingRef.current = setInterval(pollStatus, 2000);
    } catch (err) {
      setError(err.message);
      setRegenerating(false);
    }
  }, [patientId, pollStatus, activeCustomPrompt, effectivePromptName, activeExamId, enabled, currentLang]);

  useEffect(() => {
    if (!patientId || !enabled) {
      setLoading(false);
      return undefined;
    }

    setLoading(true);
    setError(null);
    setSummary(null);
    setRegenerating(false);

    fetchSummary().then((data) => {
      setLoading(false);
      if (data && (data.status === 'PENDING' || data.status === 'GENERATING')) {
        setRegenerating(true);
        pollingRef.current = setInterval(pollStatus, 2000);
      } else if (!data) {
        startGeneration();
      }
    });

    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    };
  }, [patientId, enabled, fetchSummary, pollStatus, startGeneration, effectivePromptName, activeExamId]);

  const handleSwitchPrompt = (newPromptName, newCustomPrompt, newScope, defaultExamId) => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
    setActivePromptName(newPromptName);
    setActiveCustomPrompt(newCustomPrompt);
    setActiveExamId(newScope === 'current_exam' ? defaultExamId : null);
  };

  const handleRegenerate = (language) => {
    startGeneration(language);
  };

  return {
    summary,
    loading,
    regenerating,
    error,
    userPrompts,
    activePromptName,
    handleSwitchPrompt,
    handleRegenerate,
    fetchSummary,
  };
}
